import { ListValidationSummary } from './list-validation-summary';

/**
 * The role that lets a user create and run events, granted by an approved organiser
 * application. Mirrors `Role.EventOrganiser` on the API — the server is the authority
 * on what it permits; this is only for deciding what to show and which routes to open.
 */
export const EVENT_ORGANISER_ROLE = 'app_event_organiser';

export type EventPublishStatus = 'draft' | 'pending_review' | 'published' | 'cancelled';

export type EventPairingMode = 'swiss' | 'round_robin' | 'random';

export type EventRegistrationStatus = 'active' | 'pending' | 'waitlist' | 'withdrawn' | 'removed';

/** 'accepted' is an organiser signing a submitted list off -- optional, so its absence
 *  still means "not yet looked at" rather than rejected. */
export type EventListSubmissionStatus = 'not_submitted' | 'submitted' | 'accepted' | 'rejected';

export type EventResultOutcome = 'A_WIN' | 'B_WIN' | 'DRAW';

export type EventResultStatus = 'pending' | 'confirmed' | 'disputed' | 'overridden';

export type EventRankingApprovalState = 'pending' | 'approved' | 'rejected' | 'not_required';

export interface EventListSlot {
    /** Assigned by the edit form's array control; absent on a slot that has
     * never been saved through it. The stable identity submitList/withdrawList
     * match on, since label is free text an organiser can rename at any time. */
    id?: number;
    label: string;
    required: boolean;
    pointsLimit?: number;
    orderDiceLimit?: number;
}

export interface EventResultFieldDef {
    /** Auto-assigned by the array-field editor, exactly like EventListSlot.id — absent
     * on an item that has never been saved through the admin form yet. */
    id?: number;
    label: string;
    type: 'number';
    /** Who supplies this field's number and what it means for scoring. Defaults to
     *  'none' server-side when absent. See EventResultFieldDef in event.schema.ts. */
    outcomeMode?: 'none' | 'from_outcome' | 'derives_outcome';
    /** Required, and only meaningful, when outcomeMode is 'from_outcome'. */
    outcomeValues?: { win: number; draw: number; loss: number };
    byeValue: number;
}

export interface EventScheduleBlock {
    label: string;
    type: 'round' | 'break' | 'registration' | 'results' | 'day_end';
    startTime: string;
    /** How long the block runs. Absent on blocks saved before the field existed. */
    durationMinutes?: number;
    /** Free text from the organiser - the round's scenario, house rules, where lunch is. */
    description?: string;
}

/**
 * A free-form block of event information the organiser headlines themselves - parking, food,
 * accessibility, house rules.
 *
 * Deliberately not a fixed set of named fields: the heading sits above prose the organiser wrote
 * in their own language, so a translatable label buys nothing, and every fixed category invites
 * the one nobody thought of.
 */
export interface EventInfoSection {
    heading: string;
    body?: string;
}

export interface EventLocation {
    countryCode: string;
    venueName?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    coordinates?: { type: 'Point'; coordinates: [number, number] };
}

export interface EventRanking {
    eligible: boolean;
    approvalState: EventRankingApprovalState;
    approvedBy?: string;
    approvedAt?: string;
}

export interface EventPublishReview {
    reviewedBy?: string;
    reviewedAt?: string;
    reviewNotes?: string;
}

export interface EventParticipant {
    userId?: string;
    guestName?: string;
    displayName?: string;
}

export interface CoOrganiserDetail {
    userId: string;
    displayName?: string;
}

export interface AppEvent {
    _id?: string;
    gameId: string;
    title: string;
    location: EventLocation;
    startDate: string;
    endDate?: string;
    pointsLimit?: number;
    numberOfRounds: number;
    /** Tables the venue has. Absent means one table per concurrent game, as before the field existed. */
    numberOfTables?: number;
    pairingMode: EventPairingMode;
    capacity: number;
    waitlistEnabled: boolean;
    autoPromoteWaitlist: boolean;
    visibility: 'public' | 'private';
    registrationApprovalMode: 'auto' | 'manual';
    listSlots: EventListSlot[];
    listSubmissionDate?: string;
    paymentInstructions?: string;
    /** Defaults to the creating organiser's account email but is editable — may be a
     * private relay/alias. Shown to registrants as a way to reach the organiser. */
    contactEmail?: string;
    eventPackFileId?: string;
    timeZone?: string;
    ticketPrice?: number;
    currency?: string;
    description?: string;
    imageId?: string;
    coOrganisers?: string[];
    publishStatus: EventPublishStatus;
    ranking: EventRanking;
    publishReview?: EventPublishReview;
    organiserId?: string;
    organiserDisplayName?: string;
    coOrganiserDetails?: CoOrganiserDetail[];
    gameSpecificData?: Record<string, any>;
    scheduleBlocks?: EventScheduleBlock[];
    infoSections?: EventInfoSection[];
    revealOpponentList?: boolean;
    requireCheckIn?: boolean;
    /** When true, a non-subscriber cannot register — the organiser requires in-app lists only. */
    requireInAppLists?: boolean;
    /** When true, an in-app list with no validation errors or warnings is accepted on
     *  submission. Text lists are never auto-accepted — there is nothing to validate. */
    autoAcceptCleanLists?: boolean;
    resultScoringRules?: string;
    resultFields?: EventResultFieldDef[];
    /** Active-registration count, joined in by the list/browse endpoints only (EventService.findAll)
     * — never present on a single-event fetch (findById), since it costs an extra aggregate query. */
    registeredCount?: number;
    /** Both set by mongoose (timestamps: true) on the event, and returned unprojected by
     *  findById, so they are already in the payload. The generated event pack prints updatedAt,
     *  so a printed copy can be told apart from a newer one. */
    createdAt?: string;
    updatedAt?: string;
}

export interface EventRegistration {
    _id?: string;
    eventId: string;
    userId: string;
    displayName: string;
    /** Where the organiser should contact this registrant about this event. Captured at
     *  registration and editable by the registrant; may differ from their account email,
     *  which is what an Apple/Google private-relay address makes necessary. */
    contactEmail?: string;
    status: EventRegistrationStatus;
    paymentReference?: string;
    listSubmissions?: EventListSubmission[];
    registeredAt?: string;
    withdrawnAt?: string;
    /** Set only when status is 'removed' — always present then, since the API requires a reason. */
    removalNotes?: string;
    createdAt?: string;
    checkedInAt?: string;
    readyForRound?: number;
    /** Only ever populated for the organiser/co-organisers viewing their own
     * event's registrations (EventService.listRegistrations) - joined at read
     * time from the registrant's own user record, never stored on the
     * registration itself, and never returned to any other caller. */
    email?: string;
    /** Set only on registrations created by the admin test-data generator. */
    isTestData?: boolean;
}

export type EventListSubmissionKind = 'force' | 'text';

export interface EventListSubmission {
    /** The slot's stable id at the time of registration, when the slot had one —
     * absent on a registration made before slotId existed. */
    slotId?: number;
    slotLabel: string;
    submissionKind?: EventListSubmissionKind;
    forceId?: string;
    snapshotData?: any;
    /** Absent on submissions made before validation summaries existed — render those as
     * "unknown", never as "clean". Overwritten in place when an organiser re-checks. */
    validationSummary?: ListValidationSummary;
    textSubmission?: string;
    status: EventListSubmissionStatus;
    toNotes?: string;
    /** Who accepted or rejected this list. Always an organiser or co-organiser, so the
     *  event's own organiser details resolve the name without a lookup. */
    decidedByUserId?: string;
    decidedAt?: string;
    /** Accepted by the event's auto-accept rule rather than by a person. */
    decidedAutomatically?: boolean;
    /** Handed back in after a rejection and not yet ruled on again. */
    resubmittedAfterRejection?: boolean;
    submittedAt?: string;
}

/**
 * Somewhere this force can be accepted or rejected by the viewing organiser. Several when
 * the same list has been submitted to more than one of their events -- which the API cannot
 * disambiguate, so the page asks.
 */
export interface ForceReviewTarget {
    eventId: string;
    eventTitle: string;
    eventStartDate: string;
    registrationId: string;
    playerDisplayName: string;
    slotId?: number;
    slotLabel: string;
    status: EventListSubmissionStatus;
    toNotes?: string;
}

export interface ForceEventSubmission {
    eventId: string;
    eventTitle: string;
    slotLabel: string;
    registrationId: string;
    listSubmissionDate?: string;
    pastDeadline: boolean;
    /** Included so callers can tell a rejection apart from never having submitted. Whether
     *  a status locks the force is decided by isForceLocked, not by presence alone. */
    status: EventListSubmissionStatus;
    /** The organiser's note, so a rejected list can show why. */
    toNotes?: string;
    slotId?: number;
}

export interface EventRound {
    _id?: string;
    eventId: string;
    roundNumber: number;
    pairings: EventPairing[];
    status: 'pending' | 'in_progress' | 'completed';
    startedAt?: string;
    completedAt?: string;
}

export interface EventPairing {
    _id?: string;
    participantA: EventParticipant;
    participantB?: EventParticipant;
    tableNumber?: number;
    isBye: boolean;
    result?: EventResult;
}

/** One participant's account of a game, always in participant A/B terms. */
export interface EventResultClaim {
    outcome: EventResultOutcome;
    participantAValues?: Record<string, number>;
    participantBValues?: Record<string, number>;
    reportedByUserId?: string;
    reportedAt?: string;
}

export interface EventResult {
    outcome: EventResultOutcome;
    participantAValues?: Record<string, number>;
    participantBValues?: Record<string, number>;
    /** Each side's own account. Both are kept so a dispute shows what each player
     *  claimed, rather than only whichever was reported most recently. */
    participantAClaim?: EventResultClaim;
    participantBClaim?: EventResultClaim;
    status: EventResultStatus;
    participantAReported?: boolean;
    participantBReported?: boolean;
    overriddenByTo?: boolean;
}

export interface EventStanding {
    participant: EventParticipant;
    displayName: string;
    resultTotals: Record<string, number>;
    wins: number;
    losses: number;
    draws: number;
    rank: number;
}

export interface UserNotification {
    id: string;
    type: 'co_organiser_added' | 'new_registration' | 'registration_status_changed';
    eventId: string;
    eventTitle: string;
    createdAt: string;
    read: boolean;
}

export type EventOrganiserApplicationStatus = 'pending' | 'approved' | 'rejected';

export interface EventOrganiserApplication {
    _id?: string;
    userId: string;
    contactName: string;
    contactEmail: string;
    wantsPaidEvents: boolean;
    status: EventOrganiserApplicationStatus;
    appliedAt: string;
    reviewedBy?: string;
    reviewedAt?: string;
    reviewNotes?: string;
}
